<script setup>
import { onBeforeUnmount, onMounted, ref } from 'vue'
import { RouterView } from 'vue-router'
import ShiftForm from './components/ShiftForm.vue'
import ConfirmDialog from './components/common/ConfirmDialog.vue'
import GlobalSearchDialog from './components/common/GlobalSearchDialog.vue'
import SkeletonLoader from './components/common/SkeletonLoader.vue'
import ToastContainer from './components/common/ToastContainer.vue'
import ResponsiveDrawer from './components/layout/ResponsiveDrawer.vue'
import { useAppStore } from './composables/useAppStore'
import { useConfirm } from './composables/useConfirm'
import { useGlobalSearch } from './composables/useGlobalSearch'
import { useToast } from './composables/useToast'
import AppLayout from './layouts/AppLayout.vue'

const { state, refresh, save, closeShiftForm } = useAppStore()
const confirm = useConfirm()
const toast = useToast()
const search = useGlobalSearch()
const shiftDirty = ref(false)
const shiftSaving = ref(false)

function handleGlobalKeydown(event) {
  if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
    event.preventDefault()
    search.open()
  }
}

onMounted(() => {
  refresh()
  window.addEventListener('keydown', handleGlobalKeydown)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', handleGlobalKeydown)
})

async function submitGlobalShift(payload) {
  if (shiftSaving.value) return
  shiftSaving.value = true
  try {
    const isEditing = Boolean(payload.id)
    await save('shifts', payload)
    shiftDirty.value = false
    closeShiftForm()
    toast.success(isEditing ? 'Изменения смены сохранены' : 'Смена сохранена')
  } catch (error) {
    toast.error(error?.message || 'Не удалось сохранить смену')
  } finally {
    shiftSaving.value = false
  }
}

async function requestCloseShiftForm() {
  if (shiftSaving.value) return
  if (shiftDirty.value) {
    const accepted = await confirm.ask({
      title: 'Закрыть форму?',
      message: 'Введённые изменения не сохранены. Закрыть форму и потерять их?',
      confirmText: 'Закрыть без сохранения'
    })
    if (!accepted) return
  }
  shiftDirty.value = false
  closeShiftForm()
}

function downloadDiagnostics() {
  const payload = {
    app: 'ДоходПро',
    createdAt: new Date().toISOString(),
    error: state.error,
    userAgent: navigator.userAgent,
    online: navigator.onLine,
    indexedDbAvailable: 'indexedDB' in window,
    storageEstimateSupported: Boolean(navigator.storage?.estimate)
  }
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = `dohodpro-diagnostics-${new Date().toISOString().slice(0, 10)}.json`
  link.click()
  URL.revokeObjectURL(url)
}
</script>

<template>
  <AppLayout>
    <div v-if="!state.ready && !state.error" class="grid min-h-[55vh] place-items-center" aria-live="polite" aria-busy="true">
      <div class="w-full max-w-4xl rounded-app-card border border-app-border bg-app-surface p-6 shadow-app-soft">
        <div class="mb-5 h-7 w-48 animate-pulse rounded-lg bg-slate-200"></div>
        <SkeletonLoader :rows="4" />
        <p class="mt-5 text-sm font-bold text-app-muted">Загружаем локальные данные…</p>
      </div>
    </div>

    <section v-else-if="state.error" class="grid min-h-[55vh] place-items-center" role="alert">
      <div class="w-full max-w-2xl rounded-app-card border border-danger-200 bg-white p-6 shadow-app-soft sm:p-8">
        <div class="flex h-12 w-12 items-center justify-center rounded-app-control bg-danger-50 text-xl font-black text-danger-700">!</div>
        <h2 class="mt-4 text-2xl font-black text-app-text">Не удалось открыть локальные данные</h2>
        <p class="mt-2 text-sm font-semibold leading-6 text-app-muted">{{ state.error }}</p>
        <p class="mt-2 text-sm font-semibold leading-6 text-app-muted">Проверьте, разрешено ли браузеру использовать локальное хранилище и IndexedDB для этого сайта.</p>
        <div class="mt-5 flex flex-col gap-2 sm:flex-row">
          <button type="button" class="primary-btn" :disabled="state.loading" @click="refresh">{{ state.loading ? 'Повторная загрузка…' : 'Повторить загрузку' }}</button>
          <button type="button" class="secondary-btn" @click="downloadDiagnostics">Скачать диагностику</button>
        </div>
      </div>
    </section>

    <RouterView v-else />
  </AppLayout>

  <ResponsiveDrawer
    :open="state.ui.shiftFormOpen"
    :title="state.ui.editingShift?.id ? 'Редактирование смены' : 'Добавление смены'"
    :close-on-backdrop="!shiftSaving"
    @request-close="requestCloseShiftForm"
  >
    <ShiftForm
      :model-value="state.ui.editingShift"
      :conditions="state.conditions"
      :submit-label="state.ui.editingShift?.id ? 'Сохранить изменения' : 'Сохранить смену'"
      :loading="shiftSaving"
      @submit="submitGlobalShift"
      @cancel="requestCloseShiftForm"
      @dirty-change="shiftDirty = $event"
    />
  </ResponsiveDrawer>

  <GlobalSearchDialog />
  <ConfirmDialog />
  <ToastContainer />
</template>
